import { LoadingButton } from '@mui/lab';
import { Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { useState } from 'react';

import { isChatCallError, requestChatHandoff } from 'external/firebase/controllers/chat';
import useLocales from 'hooks/useLocales';

const MAX_COMMENT_CHARS = 2000;

interface ChatHandoffDialogProps {
    open: boolean;
    sessionId: string | null;
    testAs: string[] | null;
    onClose: () => void;
}

const ChatHandoffDialog: React.FunctionComponent<ChatHandoffDialogProps> = ({ open, sessionId, testAs, onClose }) => {
    const { translate } = useLocales();
    const { enqueueSnackbar } = useSnackbar();
    const [comment, setComment] = useState('');
    const [sending, setSending] = useState(false);

    const handleSend = async (): Promise<void> => {
        if (!sessionId) {
            return;
        }
        setSending(true);
        const res = await requestChatHandoff(sessionId, comment.trim(), testAs);
        setSending(false);
        if (isChatCallError(res)) {
            const key = res.code === 'chat/handoff-limit' ? 'chat_handoff_limit' : 'something_went_wrong';
            enqueueSnackbar(translate(key), { variant: 'error' });
            return;
        }
        enqueueSnackbar(translate('chat_handoff_sent'), { variant: 'success' });
        setComment('');
        onClose();
    };

    return (
        <Dialog open={open} onClose={sending ? undefined : onClose} maxWidth='xs' fullWidth>
            <DialogTitle>{translate('chat_handoff_title')}</DialogTitle>
            <DialogContent>
                <Typography variant='body2' color='text.secondary' sx={{ mt: 1, mb: 2 }}>
                    {translate('chat_handoff_body')}
                </Typography>
                <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    maxRows={8}
                    value={comment}
                    onChange={event => setComment(event.target.value.slice(0, MAX_COMMENT_CHARS))}
                    label={translate('chat_handoff_comment')}
                />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
                <LoadingButton variant='outlined' onClick={onClose} disabled={sending}>
                    {translate('cancel')}
                </LoadingButton>
                <LoadingButton variant='contained' loading={sending} onClick={handleSend}>
                    {translate('chat_handoff_send')}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

export default ChatHandoffDialog;
